import { differenceInCalendarDays, startOfDay } from "date-fns";

import { type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";

import yup from "utilities/form/yup";

import { type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";

import { BTLoginTypes, Statuses } from "handlers/models";

/** Returns display text for a Selection Option status. */
export function getStatusTextForSelectionOption(status: number, isBuilderPortal: boolean): string {
    switch (status) {
        case Statuses.APPROVED:
            return "Approved";
        case Statuses.PENDING:
            return isBuilderPortal ? "Sent" : "Pending";
        case Statuses.DECLINED:
            return "Declined";
        case Statuses.DELETED:
            return "Deleted";
        case Statuses.UNRELEASED:
        default:
            return "Draft";
    }
}

/** Returns a BDS badge display type token for UI coloring of a Selection Option */
export function getBdsStatusTypeForSelectionOption(
    status: number,
    isBuilderPortal: boolean,
): TBTStatusBadgeDisplayTypes {
    switch (status) {
        case Statuses.APPROVED:
            return "success";
        case Statuses.DECLINED:
        case Statuses.DELETED:
            return "danger";
        case Statuses.PENDING:
            return isBuilderPortal ? "info" : "warning";
        case Statuses.UNRELEASED:
            return "default";
        default:
            return "info";
    }
}

/** Returns a StatusType token for UI coloring of a Selection Option */
export function getStatusTypeForSelectionOption(
    status: number,
    isBuilderPortal: boolean,
): StatusTypes {
    switch (status) {
        case Statuses.APPROVED:
            return "success";
        case Statuses.DECLINED:
        case Statuses.DELETED:
            return "danger";
        case Statuses.PENDING:
            return isBuilderPortal ? "info" : "pending";
        case Statuses.UNRELEASED:
            return "";
        default:
            return "info";
    }
}

/**
 * Number of calendar days from today (inclusive) within which a pending selection option's due
 * date is considered "Due soon" on the client portal. Centralized so it can become configurable
 * (per-job / per-account) later without hunting down the comparison.
 */
export const SELECTION_OPTION_DUE_SOON_THRESHOLD_DAYS = 7;

export enum ClientDeadlineState {
    Overdue = "overdue",
    DueSoon = "dueSoon",
    None = "none",
}

/**
 * Classifies a pending selection option's due date relative to today for the client portal.
 * The API returns deadlineDate in the builder's local timezone, so comparing against the local
 * start-of-day keeps the day boundary correct. Returns "overdue" when the due date has passed,
 * "dueSoon" when it falls today or within {@link SELECTION_OPTION_DUE_SOON_THRESHOLD_DAYS} days,
 * otherwise "none".
 */
export function getClientDeadlineState(
    deadlineDate: Date | string | null | undefined,
): ClientDeadlineState {
    if (!deadlineDate) {
        return ClientDeadlineState.None;
    }
    const daysUntilDue = differenceInCalendarDays(
        startOfDay(new Date(deadlineDate)),
        startOfDay(new Date()),
    );
    if (daysUntilDue < 0) {
        return ClientDeadlineState.Overdue;
    }
    if (daysUntilDue <= SELECTION_OPTION_DUE_SOON_THRESHOLD_DAYS) {
        return ClientDeadlineState.DueSoon;
    }
    return ClientDeadlineState.None;
}

/** Helper to derive both text and type at once */
export function getSelectionOptionStatusDisplay(
    status: number,
    isBuilderPortal: boolean,
    clientDeadline?: { isClient: boolean; deadlineDate: Date | string | null | undefined },
): {
    text: string;
    type: TBTStatusBadgeDisplayTypes;
} {
    // On the client portal, an undecided (pending) option surfaces its due-date urgency:
    // "Overdue" (styled like Declined) and "Due soon" (styled like Pending). The builder portal
    // and already-decided statuses are unaffected.
    if (clientDeadline?.isClient && status === Statuses.PENDING) {
        const deadlineState = getClientDeadlineState(clientDeadline.deadlineDate);
        if (deadlineState === ClientDeadlineState.Overdue) {
            return { text: "Overdue", type: "danger" };
        }
        if (deadlineState === ClientDeadlineState.DueSoon) {
            return { text: "Due soon", type: "warning" };
        }
    }
    return {
        text: getStatusTextForSelectionOption(status, isBuilderPortal),
        type: getBdsStatusTypeForSelectionOption(status, isBuilderPortal),
    };
}

/**
 * Registrable domains of retailers whose product pages our scraping provider can't read.
 */
export const UNSUPPORTED_PRODUCT_URL_HOSTS = ["lowes.com", "homedepot.com", "vakkerlight.com"];

/** Validation copy shown when a product URL points to a retailer we can't auto-fill from. */
export const UNSUPPORTED_PRODUCT_URL_MESSAGE =
    "This website doesn't allow autofill. Enter details manually instead.";
/** Validation copy shown when the product URL isn't a valid URL, so autofill can't run. */
export const INVALID_PRODUCT_URL_MESSAGE = "Enter a valid URL to autofill.";
export const DEFAULT_SCRAPING_ERROR_MESSAGE =
    "Couldn't autofill from the provided URL. Please check the link or fill in the form manually.";

/**
 * Success message toast string for applyParseResultToForm
 */
export const APPLY_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE: string =
    "Selection option filled from URL. Review the details before saving.";

/**
 * Success message toast string for applyDocumentParseResultToForm
 */
export const APPLY_DOCUMENT_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE: string =
    "Selection option filled from document. Review the details before saving.";

/**
 * Returns true when the URL's host is (or is a subdomain of) a retailer on
 * {@link UNSUPPORTED_PRODUCT_URL_HOSTS}. Returns false for empty/unparseable input.
 */
export function isUnsupportedProductUrlHost(url: string | undefined | null): boolean {
    if (!url) {
        return false;
    }

    let host: string;
    try {
        host = new URL(url).hostname.toLowerCase();
    } catch {
        return false;
    }

    // Match on exact host or any subdomain
    return UNSUPPORTED_PRODUCT_URL_HOSTS.some(
        (domain) => host === domain || host.endsWith(`.${domain}`),
    );
}

export const AUTOFILL_ALLOWED_FILE_TYPES = ["pdf", "docx", "xlsx"];

export const autofillUrlSchema = yup
    .string()
    .url("Please enter a valid URL")
    .nullable()
    .test(
        "unsupported-host",
        UNSUPPORTED_PRODUCT_URL_MESSAGE,
        (value: string | null | undefined) => !isUnsupportedProductUrlHost(value),
    );

/** Helper to determine if the new approve and decline selection option actions should be shown */
export function shouldShowApproveAndDeclineActionsColumn(
    loginType: BTLoginTypes,
    hasSelectionOptionsEditPermission?: boolean,
): boolean {
    if (loginType === BTLoginTypes.BUILDER) {
        return !!hasSelectionOptionsEditPermission;
    } else if (loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT) {
        return true;
    }

    return false;
}
